import type { FormEvent, ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import type { Answer, AskScope, Citation } from '@contracts/ask';
import {
  useAnswer,
  useAnswers,
  useAsk,
  useKeepAnswer,
  useMeeting,
  useTranscript,
} from '../api/queries';
import { BackLink, Button, Card, Empty, SkeletonRows } from '../ui';
import { initialsOf } from '../ui';
import { between, since } from '../lib/time';
import { isFreeMonthOver } from '../api/client';

/**
 * Board 12 — an answer is a page with its own URL, never a side panel.
 *
 * Three ways in: `/ask?q=…&scope=…&scopeId=…` (everything, a space, a
 * series), `/m/:id/answer?q=…` (these forty-eight minutes), and
 * `/answers/:answerId` (one kept or recent answer). The strip above the
 * answer says how it looked before the answer does — what was read,
 * complete or sampled, how many meetings this person could not open —
 * because "we never discussed this" and "I did not find it" are different
 * answers. A citation lights the turn where it lives, in the transcript
 * pane, with the lines around it; nothing pops up over the lines it quotes.
 */
export function AnswerPage(): ReactElement {
  const { id: meetingId, answerId } = useParams<{ id?: string; answerId?: string }>();
  const [params] = useSearchParams();
  const q = params.get('q')?.trim() ?? '';
  const scope: AskScope = meetingId
    ? 'meeting'
    : ((['all', 'space', 'series'] as const).find((s) => s === params.get('scope')) ?? 'all');
  const scopeId = meetingId ?? params.get('scopeId') ?? null;

  const ask = useAsk();
  const stored = useAnswer(answerId ?? null);
  const asked = useRef<string | null>(null);
  useEffect(() => {
    if (!q || answerId || asked.current === q) return;
    asked.current = q;
    ask.mutate({ question: q, scope, ...(scopeId ? { scopeId } : {}) });
    // One shot per question; re-asking is a new q, and the ref keeps a re-render from asking twice.
  }, [q, answerId, scope, scopeId, ask]);

  const answer: Answer | null = answerId ? (stored.data ?? null) : (ask.data ?? null);
  const effectiveScope = answer?.scope ?? scope;
  const effectiveScopeId = answer?.scopeId ?? scopeId;
  const words = useScopeWords(effectiveScope, effectiveScopeId);

  return (
    <div className="page stack-lg">
      <Toolbar scope={effectiveScope} scopeId={effectiveScopeId} answer={answer} />
      {answerId && stored.isPending ? (
        <SkeletonRows rows={4} />
      ) : answerId && stored.isError ? (
        <Card flush>
          <Empty tone="none" lead="No such answer." />
        </Card>
      ) : ask.isPending ? (
        <Looking question={q} scope={scope} />
      ) : ask.isError && !answer && isFreeMonthOver(ask.error) ? (
        // Not a fault, and trying again would not change it: the api says why
        // and whom to write to (2026-09-26).
        <Card flush>
          <Empty tone="none" lead="Ptitim no longer answers questions for you.">
            {ask.error.message}
          </Empty>
        </Card>
      ) : ask.isError && !answer ? (
        <Card flush>
          <Empty
            tone="bad"
            lead="The answer could not be written."
            action={
              <Button
                onClick={() => ask.mutate({ question: q, scope, ...(scopeId ? { scopeId } : {}) })}
              >
                Try again
              </Button>
            }
          >
            {(ask.error as Error).message}
          </Empty>
        </Card>
      ) : answer ? (
        <AnswerView answer={answer} />
      ) : (
        <Card flush>
          <Empty tone="none" lead={`Ask something about ${words}.`}>
            What was decided, why, who said what, whether something ever came up. The answer says
            how it looked and cites what it used.
          </Empty>
        </Card>
      )}
      <Saved scope={effectiveScope} scopeId={effectiveScopeId} current={answer?.id ?? null} />
      <AskBox
        scope={effectiveScope}
        scopeId={effectiveScopeId}
        placeholder={`Ask something${answer ? ' else' : ''} about ${words}…`}
        autoFocus={!q && !answerId}
      />
    </div>
  );
}

function Toolbar({
  scope,
  scopeId,
  answer,
}: {
  scope: AskScope;
  scopeId: string | null;
  answer: Answer | null;
}): ReactElement {
  const keep = useKeepAnswer();
  const back =
    scope === 'meeting' && scopeId
      ? { to: `/m/${scopeId}`, label: 'The meeting' }
      : scope === 'series' && scopeId
        ? { to: `/series/${encodeURIComponent(scopeId)}`, label: 'The series' }
        : scope === 'space' && scopeId
          ? { to: `/meetings?space=${scopeId}`, label: 'The collection' }
          : { to: '/knowledge', label: 'Knowledge' };
  return (
    <div className="toolbar">
      <BackLink to={back.to}>{back.label}</BackLink>
      <span className="crumb">Answer</span>
      <div className="actions">
        {answer ? (
          <Button
            variant={answer.keptAt ? 'secondary' : 'primary'}
            working={keep.isPending ? 'Saving…' : false}
            onClick={() => keep.mutate({ id: answer.id, keep: !answer.keptAt })}
          >
            {answer.keptAt ? 'Kept ✓' : 'Keep this answer'}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function Looking({ question, scope }: { question: string; scope: AskScope }): ReactElement {
  return (
    <div className="stack-lg">
      <div className="strip">
        <span className="chip">looking…</span>
      </div>
      <h1 className="t-title" style={{ textWrap: 'balance' }}>
        {question}
      </h1>
      <Card>
        <p className="t-secondary">
          {scope === 'meeting'
            ? 'Reading the meeting…'
            : 'Looking through what you can open — what is known, the meetings, the lines. This takes a little while.'}
        </p>
        <SkeletonRows rows={3} />
      </Card>
    </div>
  );
}

function AnswerView({ answer }: { answer: Answer }): ReactElement {
  const paragraphs = answer.body
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  const cited = answer.citations.filter((c) => c.segmentId && c.text);
  const first = cited[0] ?? null;
  const sampled = answer.strategy.filter((s) => !s.complete);
  return (
    <div className="stack-lg">
      <div className="strip" aria-label="How it looked">
        {answer.strategy.length === 0 ? (
          <span className="chip">answered without looking anything up</span>
        ) : null}
        {answer.strategy.map((s, i) => (
          <span key={i} className="chip ok">
            {s.summary}
          </span>
        ))}
        {answer.strategy.length > 0 ? (
          sampled.length === 0 ? (
            <span className="chip">complete — nothing sampled</span>
          ) : (
            <span className="chip sampled">
              searched, did not read everything ·{' '}
              {sampled.map((s) => `${Math.min(s.count, 12)} of ${s.count}`).join(' · ')}
            </span>
          )
        ) : null}
        {answer.skippedMeetings > 0 ? (
          <span className="chip hidden">
            {answer.skippedMeetings} {answer.skippedMeetings === 1 ? 'meeting' : 'meetings'} you
            cannot open
          </span>
        ) : null}
        {!answer.found ? <span className="chip">nothing found</span> : null}
      </div>
      <h1 className="t-title" style={{ textWrap: 'balance' }}>
        {answer.question}
      </h1>
      <div className={first ? 'answer-grid' : 'stack-lg'}>
        <Card>
          <div className="stack" style={{ gap: 12 }}>
            {paragraphs.map((p, i) => (
              <p key={i} className="t-body" style={{ maxWidth: '65ch' }}>
                {p}
              </p>
            ))}
            {first ? (
              <blockquote className="quote">
                <span className="plate">
                  <span className="avatar">{initialsOf(first.speakerName, '')}</span>
                  {first.speakerName ?? 'Unknown'}
                  {first.spokeAt ? ` · ${dateWords(first.spokeAt)}, ${clockOf(first.spokeAt)}` : ''}
                </span>
                <span>“{first.text}”</span>
              </blockquote>
            ) : null}
            {answer.citations.length ? (
              <div className="cluster" style={{ gap: 8, flexWrap: 'wrap' }}>
                {answer.citations.map((c, i) => (
                  <Link
                    key={i}
                    className="chip"
                    to={`/m/${c.meetingId}${c.segmentId ? `#line-${c.segmentId}` : ''}`}
                  >
                    {c.title}
                    {c.speakerName ? ` · ${c.speakerName}` : ''}
                    {c.spokeAt ? ` · ${clockOf(c.spokeAt)}` : ''}
                  </Link>
                ))}
              </div>
            ) : null}
            <p
              className="t-secondary"
              style={{ borderTop: '1px solid var(--line)', paddingTop: 10 }}
            >
              {answer.model ? `Written by ${answer.model} · ` : ''}
              {since(answer.createdAt)}
            </p>
          </div>
        </Card>
        {first ? <AtTheCitation first={first} others={cited.slice(1)} /> : null}
      </div>
    </div>
  );
}

/** The transcript at the citation: the turn it lives in, lit, with the lines around it. */
function AtTheCitation({ first, others }: { first: Citation; others: Citation[] }): ReactElement {
  const transcript = useTranscript(first.meetingId, true);
  const lines = transcript.data ?? [];
  const at = lines.findIndex((l) => l.id === first.segmentId);
  const around = at >= 0 ? lines.slice(Math.max(0, at - 1), at + 2) : [];
  return (
    <Card title="Transcript, at the citation" flush>
      {transcript.isPending ? (
        <SkeletonRows rows={3} />
      ) : around.length === 0 ? (
        <div className="cite on">
          <span className="when">
            {first.spokeAt ? clockOf(first.spokeAt) : ''} · {first.title}
          </span>
          <span>
            {first.speakerName ? <strong>{first.speakerName}: </strong> : null}“{first.text}”
          </span>
        </div>
      ) : (
        around.map((l) => (
          <div key={l.id} className={`cite ${l.id === first.segmentId ? 'on' : 'dim'}`}>
            <span className="when">{l.spokeAt ? clockOf(l.spokeAt, true) : ''}</span>
            <span>
              {l.id === first.segmentId ? (
                <>
                  <span className="plate">
                    <span className="avatar">{initialsOf(l.speakerName, '')}</span>
                    {l.speakerName ?? 'Unknown'}
                  </span>{' '}
                  “{l.text}”
                </>
              ) : (
                <span className="t-secondary">…{l.text}</span>
              )}
            </span>
          </div>
        ))
      )}
      {others.map((c, i) => (
        <div key={i} className="cite">
          <span className="when">
            {c.spokeAt ? clockOf(c.spokeAt) : ''} · {c.title}
          </span>
          <span>
            {c.speakerName ? <strong>{c.speakerName}: </strong> : null}“{c.text}”
          </span>
        </div>
      ))}
      <div className="cite">
        <Link to={`/m/${first.meetingId}#line-${first.segmentId}`} className="t-secondary">
          Open the moment →
        </Link>
      </div>
    </Card>
  );
}

function Saved({
  scope,
  scopeId,
  current,
}: {
  scope: AskScope;
  scopeId: string | null;
  current: string | null;
}): ReactElement | null {
  const kept = useAnswers({ scope, ...(scopeId ? { scopeId } : {}), kept: '1' });
  const others = (kept.data ?? []).filter((a) => a.id !== current);
  if (others.length === 0) return null;
  return (
    <div className="cluster" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <span className="t-secondary">Saved questions</span>
      {others.map((a) => (
        <Link key={a.id} to={`/answers/${a.id}`} className="chip">
          {a.question}
        </Link>
      ))}
    </div>
  );
}

/**
 * The ask box: one line, Enter asks, and the answer is a page. Shared by
 * Knowledge (everything), the meeting (these minutes), the series and this
 * page itself.
 */
export function AskBox({
  scope,
  scopeId,
  placeholder,
  autoFocus = false,
  hotkey = false,
}: {
  scope: AskScope;
  scopeId: string | null;
  placeholder: string;
  autoFocus?: boolean;
  hotkey?: boolean;
}): ReactElement {
  const navigate = useNavigate();
  const ref = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  useEffect(() => {
    if (!hotkey) return;
    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement | null)?.closest(
        'input, textarea, [contenteditable]',
      );
      if (e.key === 'a' && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        ref.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hotkey]);
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = value.trim();
    if (q.length < 3) return;
    const search = new URLSearchParams({ q });
    if (scope === 'meeting' && scopeId) {
      navigate(`/m/${scopeId}/answer?${search.toString()}`);
    } else {
      search.set('scope', scope);
      if (scopeId) search.set('scopeId', scopeId);
      navigate(`/ask?${search.toString()}`);
    }
    setValue('');
  };
  return (
    <form className="ask-box" role="search" onSubmit={submit} aria-label="Ask">
      <span className="spark" aria-hidden="true">
        ✦
      </span>
      <input
        ref={ref}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        aria-label="Ask a question"
        autoFocus={autoFocus}
      />
      {hotkey ? <span className="kbd">A</span> : <span className="kbd">↵</span>}
    </form>
  );
}

/** "these 48 minutes" for a meeting, the series' name, the space, or everything. */
function useScopeWords(scope: AskScope, scopeId: string | null): string {
  const meeting = useMeeting(scope === 'meeting' && scopeId ? scopeId : '');
  if (scope === 'meeting') {
    const m = meeting.data;
    const minutes = m?.endedAt ? Math.round(between(m.startedAt, m.endedAt) / 60) : 0;
    return minutes > 0 ? `these ${minutes} minutes` : 'this meeting';
  }
  if (scope === 'series') return scopeId ? `every ${scopeId.replace(/-/g, ' ')}` : 'this series';
  if (scope === 'space') return 'this space';
  return 'everything we know';
}

function clockOf(iso: string, seconds = false): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    ...(seconds ? { second: '2-digit' } : {}),
    hour12: false,
  });
}

function dateWords(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
}

/** The meeting page's toolbar button: "Ask these 48 minutes". */
export function AskTheseMinutes({ meetingId }: { meetingId: string }): ReactElement {
  const words = useScopeWords('meeting', meetingId);
  return (
    <Link to={`/m/${meetingId}/answer`} className="btn">
      {words === 'this meeting' ? 'Ask this meeting' : `Ask ${words}`}
    </Link>
  );
}
